import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Zap } from "lucide-react";
import { useLang } from "./LanguageContext";
import { TRX_URL, LOGO_URL } from "./content";

const NavLink = ({ href, children, onClick }) => (
  <a
    href={href}
    onClick={onClick}
    data-testid={`nav-${children.toString().toLowerCase().replace(/\s/g, "-")}`}
    className="group relative text-sm font-medium text-zinc-300 hover:text-white transition-colors"
  >
    {children}
    <span className="absolute -bottom-1 left-0 h-px w-0 bg-[#ccff00] transition-all duration-300 group-hover:w-full" />
  </a>
);

export const Header = ({ bannerOpen = false }) => {
  const { t, lang, toggle } = useLang();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const links = [
    { href: "#products", label: t.nav.products },
    { href: "#features", label: t.nav.features },
    { href: "#how", label: t.nav.how },
    { href: "#faq", label: t.nav.faq },
    { href: "#contact", label: t.nav.contact },
  ];

  return (
    <motion.header
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      transition={{ duration: 1, ease: [0.16, 1, 0.3, 1], delay: 0.2 }}
      className={`fixed left-0 right-0 z-50 transition-[top,background-color] duration-500 ${
        bannerOpen ? "top-11" : "top-0"
      } ${
        scrolled ? "bg-black/70 backdrop-blur-xl border-b border-white/10" : "bg-transparent border-b border-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 flex items-center justify-between h-20">
        <a href="#top" data-testid="brand-logo" className="flex items-center gap-3">
          <img src={LOGO_URL} alt="NEXCIP" className="h-9 w-9 object-cover rounded-sm" />
          <div className="leading-none">
            <span className="font-display font-black text-lg tracking-tight text-white">NEXCIP</span>
            <span className="block text-[9px] tracking-[0.25em] text-zinc-500 uppercase">Lima Cipta Digital</span>
          </div>
        </a>

        <nav className="hidden lg:flex items-center gap-10">
          {links.map((l) => (
            <NavLink key={l.href} href={l.href}>{l.label}</NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <button
            data-testid="lang-toggle"
            onClick={toggle}
            className="hidden sm:flex items-center h-9 px-3 border border-white/15 text-xs font-bold tracking-wider text-zinc-300 hover:border-[#ccff00] hover:text-[#ccff00] transition-colors"
          >
            {lang.toUpperCase()}
          </button>
          <a
            href={TRX_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="header-login-btn"
            className="hidden sm:inline-flex items-center h-9 px-4 text-sm font-medium text-white hover:text-[#ccff00] transition-colors"
          >
            {t.cta.login}
          </a>
          <a
            href={TRX_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="header-register-btn"
            className="hidden sm:inline-flex items-center gap-1.5 h-9 px-4 bg-[#ccff00] text-black text-sm font-bold hover:bg-[#b3e600] transition-colors"
          >
            <Zap className="h-3.5 w-3.5" /> {t.cta.register}
          </a>
          <button
            data-testid="mobile-menu-toggle"
            onClick={() => setOpen((o) => !o)}
            className="lg:hidden text-white p-1"
            aria-label="Menu"
          >
            {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
            className="lg:hidden overflow-hidden bg-black/90 backdrop-blur-xl border-b border-white/10"
          >
            <div className="px-6 py-6 flex flex-col gap-5">
              {links.map((l) => (
                <a
                  key={l.href}
                  href={l.href}
                  onClick={() => setOpen(false)}
                  data-testid={`mobile-nav-${l.label.toLowerCase().replace(/\s/g, "-")}`}
                  className="text-lg font-display font-semibold text-white"
                >
                  {l.label}
                </a>
              ))}
              <div className="flex items-center gap-3 pt-2">
                <button
                  data-testid="mobile-lang-toggle"
                  onClick={toggle}
                  className="flex items-center h-11 px-4 border border-white/15 text-sm font-bold text-zinc-200"
                >
                  {lang.toUpperCase()}
                </button>
                <a
                  href={TRX_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="mobile-login-btn"
                  className="flex-1 flex items-center justify-center h-11 border border-white/20 text-sm font-medium text-white"
                >
                  {t.cta.login}
                </a>
                <a
                  href={TRX_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="mobile-register-btn"
                  className="flex-1 flex items-center justify-center h-11 bg-[#ccff00] text-black text-sm font-bold"
                >
                  {t.cta.register}
                </a>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
};
